import type { GetFanClubStatusResponse, ShowSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { isPrivateQueueAvailable } from '@fetmode/utils';

import { isPartyLobby, isTicketLobby } from './show-room-access.js';
import { hasShowFloorAccess } from './show-join-gate.js';

export type ShowPromoAction =
  | 'signup'
  | 'join'
  | 'spy'
  | 'flashSpy'
  | 'queue'
  | 'fanClub'
  | 'videos';

export type ShowPromoSlide = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  action: ShowPromoAction;
};

function pushGuestSlides(slides: ShowPromoSlide[], show: ShowSummary): void {
  const typeLabel = show.access.typeLabel;
  slides.push({
    id: 'signup',
    eyebrow: t('common.show.promoAdEyebrow'),
    title: t('common.show.promoSignupTitle'),
    body: t('common.show.promoSignupBody').replace('{{type}}', typeLabel),
    ctaLabel: t('common.show.promoSignupCta'),
    action: 'signup',
  });
  slides.push({
    id: 'signup-tip',
    eyebrow: t('common.show.promoAdEyebrow'),
    title: t('common.show.promoSignupTipTitle'),
    body: t('common.show.promoSignupTipBody'),
    ctaLabel: t('common.show.promoSignupCta'),
    action: 'signup',
  });
  slides.push({
    id: 'videos-guest',
    eyebrow: t('common.show.promoAdEyebrow'),
    title: t('common.show.promoVideosTitle').replace('{{name}}', show.model.displayName),
    body: t('common.show.promoVideosBody'),
    ctaLabel: t('common.show.promoVideosCta'),
    action: 'videos',
  });
}

function pushVideosSlide(
  slides: ShowPromoSlide[],
  show: ShowSummary,
  modelUsername: string,
): void {
  slides.push({
    id: 'videos',
    eyebrow: t('common.show.promoAdEyebrow'),
    title: t('common.show.promoVideosTitle').replace('{{name}}', show.model.displayName),
    body: t('common.show.promoVideosBodyNamed').replace('{{username}}', modelUsername),
    ctaLabel: t('common.show.promoVideosCta'),
    action: 'videos',
  });
}

/** Delayed nag slides for Main Stage — stream stays clear until the nag fires. */
function buildMainStageNagSlides(input: {
  show: ShowSummary;
  user: { id: string } | null;
  modelUsername: string;
}): ShowPromoSlide[] {
  const { show, user, modelUsername } = input;
  const slides: ShowPromoSlide[] = [];

  if (!user) {
    pushGuestSlides(slides, show);
    return slides;
  }

  if (isPrivateQueueAvailable(show)) {
    slides.push({
      id: 'queue',
      eyebrow: t('common.show.promoNagEyebrow'),
      title: t('common.show.promoQueueTitle'),
      body: t('common.show.promoQueueBody').replace('{{type}}', show.access.typeLabel),
      ctaLabel: t('common.show.joinGateGetInLineButton'),
      action: 'queue',
    });
  }

  pushVideosSlide(slides, show, modelUsername);
  return slides;
}

function pushFlashSpySlide(slides: ShowPromoSlide[], show: ShowSummary): void {
  if (
    show.type === 'EXCLUSIVE' ||
    !show.access.flashSpyEnabled ||
    show.access.flashSpyCoins === null ||
    show.access.flashSpyCoins <= 0
  ) {
    return;
  }

  slides.push({
    id: 'flash-spy',
    eyebrow: t('common.show.promoModeEyebrow'),
    title: t('common.show.promoFlashSpyTitle'),
    body: t('common.show.promoFlashSpyBody').replace(
      '{{amount}}',
      String(show.access.flashSpyCoins),
    ),
    ctaLabel: t('common.show.joinFlashSpy').replace(
      '{{amount}}',
      String(show.access.flashSpyCoins),
    ),
    action: 'flashSpy',
  });
}

/** On-break slides — rotate immediately like Club / paid room promos. */
function buildBreakSlides(input: {
  show: ShowSummary;
  user: { id: string } | null;
  modelUsername: string;
}): ShowPromoSlide[] {
  const { show, user, modelUsername } = input;
  const slides: ShowPromoSlide[] = [];

  if (!user) {
    slides.push({
      id: 'break-signup',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoBreakTitle'),
      body: t('common.show.promoBreakBody'),
      ctaLabel: t('common.show.promoSignupCta'),
      action: 'signup',
    });
    pushGuestSlides(slides, show);
    return slides;
  }

  slides.push({
    id: 'break',
    eyebrow: t('common.show.promoModeEyebrow'),
    title: t('common.show.promoBreakTitle'),
    body: t('common.show.promoBreakBody'),
    ctaLabel: t('common.show.joinGateGetInLineButton'),
    action: 'queue',
  });

  if (isPrivateQueueAvailable(show)) {
    slides.push({
      id: 'queue',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoQueueTitle'),
      body: t('common.show.promoQueueBody').replace('{{type}}', show.access.typeLabel),
      ctaLabel: t('common.show.joinGateGetInLineButton'),
      action: 'queue',
    });
  }

  pushVideosSlide(slides, show, modelUsername);
  return slides;
}

function buildAwaySlides(input: {
  show: ShowSummary;
  user: { id: string } | null;
  modelUsername: string;
}): ShowPromoSlide[] {
  const { show, user, modelUsername } = input;
  const slides: ShowPromoSlide[] = [];

  if (!user) {
    slides.push({
      id: 'away-signup',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoAwayTitle'),
      body: t('common.show.promoAwayBody'),
      ctaLabel: t('common.show.promoSignupCta'),
      action: 'signup',
    });
    pushGuestSlides(slides, show);
    return slides;
  }

  slides.push({
    id: 'away',
    eyebrow: t('common.show.promoModeEyebrow'),
    title: t('common.show.promoAwayTitle'),
    body: t('common.show.promoAwayBody'),
    ctaLabel: t('common.show.joinGateGetInLineButton'),
    action: 'queue',
  });

  pushVideosSlide(slides, show, modelUsername);
  return slides;
}

export function buildShowPromoSlides(input: {
  show: ShowSummary;
  user: { id: string } | null;
  fanClub: GetFanClubStatusResponse | null;
  modelUsername: string;
}): ShowPromoSlide[] {
  const { show, user, fanClub, modelUsername } = input;
  const slides: ShowPromoSlide[] = [];
  const typeLabel = show.access.typeLabel;

  if (show.status === 'ENDED') {
    return slides;
  }

  if (show.status === 'PAUSED') {
    return buildBreakSlides({ show, user, modelUsername });
  }

  if (show.status === 'AWAY') {
    return buildAwaySlides({ show, user, modelUsername });
  }

  // Party / ticket lobbies: public stream is visible until the show starts — no promo overlay.
  if (isPartyLobby(show) || isTicketLobby(show)) {
    return slides;
  }

  // Main Stage: clear stream; nag slides are used after a delay in the overlay.
  if (show.type === 'MAIN_STAGE') {
    return buildMainStageNagSlides({ show, user, modelUsername });
  }

  // Soft room VIEWER on a paid private still sees promo / paywall slides.
  if (hasShowFloorAccess(show)) {
    return slides;
  }

  if (!user) {
    pushGuestSlides(slides, show);
    return slides;
  }

  const needsFanClub =
    show.access.requiresFanClub && fanClub !== null && !fanClub.isMember;

  if (needsFanClub && fanClub) {
    slides.push({
      id: 'fan-club',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoFanClubTitle'),
      body: t('common.show.promoFanClubBody').replace(
        '{{amount}}',
        String(fanClub.monthlyCoins),
      ),
      ctaLabel: t('common.show.joinFanClub').replace(
        '{{amount}}',
        String(fanClub.monthlyCoins),
      ),
      action: 'fanClub',
    });
  }

  if (show.type === 'EXCLUSIVE') {
    slides.push({
      id: 'exclusive-wait',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoExclusiveTitle'),
      body: t('common.show.promoExclusiveBody'),
      ctaLabel: t('common.show.joinGateGetInLineButton'),
      action: 'queue',
    });
  } else if (show.type === 'VIP') {
    slides.push({
      id: 'vip-join',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoVipTitle'),
      body:
        show.access.coinsPerMinute !== null
          ? t('common.show.promoVipBody').replace(
              '{{amount}}',
              String(show.access.coinsPerMinute),
            )
          : t('common.show.promoVipBodyOpen'),
      ctaLabel:
        (show.access.preTipCoins ?? 0) > 0 && show.access.coinsPerMinute !== null
          ? t('common.show.joinVipPretip')
              .replace('{{pretip}}', String(show.access.preTipCoins))
              .replace('{{amount}}', String(show.access.coinsPerMinute))
          : show.access.coinsPerMinute !== null
            ? t('common.show.joinVip').replace('{{amount}}', String(show.access.coinsPerMinute))
            : t('common.show.joinGateJoinButton'),
      action: 'join',
    });
  } else if (show.type === 'TICKET') {
    const soldOut =
      show.access.ticketCapacity !== null &&
      show.access.ticketsSold >= show.access.ticketCapacity;
    slides.push({
      id: 'ticket',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: soldOut
        ? t('common.show.promoTicketSoldOutTitle')
        : t('common.show.promoTicketTitle'),
      body: soldOut
        ? t('common.show.promoTicketSoldOutBody')
        : t('common.show.promoTicketBody'),
      ctaLabel: soldOut
        ? t('common.show.joinGateGetInLineButton')
        : t('common.show.buyTicket').replace(
            '{{amount}}',
            String(show.access.entryFeeCoins ?? 0),
          ),
      action: soldOut ? 'queue' : 'join',
    });
  } else if (show.type === 'PARTY') {
    slides.push({
      id: 'party',
      eyebrow: t('common.show.promoModeEyebrow'),
      title:
        show.status === 'LIVE'
          ? t('common.show.promoPartyLiveTitle')
          : t('common.show.promoPartyWaitTitle'),
      body: t('common.show.promoPartyBody'),
      ctaLabel:
        (show.access.preTipCoins ?? 0) > 0
          ? t('common.show.joinPartyPretip')
              .replace('{{amount}}', String(show.access.entryFeeCoins ?? 0))
              .replace('{{pretip}}', String(show.access.preTipCoins))
          : t('common.show.joinParty').replace(
              '{{amount}}',
              String(show.access.entryFeeCoins ?? 0),
            ),
      action: 'join',
    });
  } else if (show.type === 'PEEP') {
    slides.push({
      id: 'peep',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoPeepTitle'),
      body: t('common.show.promoPeepBody'),
      ctaLabel: t('common.show.joinPeep').replace(
        '{{amount}}',
        String(show.access.coinsPerMinute ?? 0),
      ),
      action: 'join',
    });
  } else if (show.type === 'CLUB' && !needsFanClub) {
    slides.push({
      id: 'club-join',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoClubTitle'),
      body: t('common.show.promoClubBody'),
      ctaLabel: t('common.show.joinGateJoinButton'),
      action: 'join',
    });
  }

  if (
    show.type !== 'EXCLUSIVE' &&
    show.type !== 'PEEP' &&
    show.access.spyEnabled &&
    show.access.spyCoinsPerMinute !== null &&
    show.access.spyCoinsPerMinute > 0
  ) {
    slides.push({
      id: 'spy',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoSpyTitle'),
      body: t('common.show.promoSpyBody').replace(
        '{{amount}}',
        String(show.access.spyCoinsPerMinute),
      ),
      ctaLabel: t('common.show.joinSpy').replace(
        '{{amount}}',
        String(show.access.spyCoinsPerMinute),
      ),
      action: 'spy',
    });
  }

  pushFlashSpySlide(slides, show);

  if (isPrivateQueueAvailable(show) && show.type !== 'EXCLUSIVE') {
    slides.push({
      id: 'queue',
      eyebrow: t('common.show.promoModeEyebrow'),
      title: t('common.show.promoQueueTitle'),
      body: t('common.show.promoQueueBody').replace('{{type}}', typeLabel),
      ctaLabel: t('common.show.joinGateGetInLineButton'),
      action: 'queue',
    });
  }

  pushVideosSlide(slides, show, modelUsername);

  return slides;
}
